import { FC, Children, cloneElement, useState, useCallback, useLayoutEffect } from 'react/';

import { subscribe, unsubscribe } from '../..';
import { IPropKeysObject, ISubscriber, TPayload } from '../../models';

import { createPropsByPayload } from './helpers';

type TCallbacks = Record<string, (args: TPayload) => void>;

interface IHandleEvent {
    payload: TPayload;
    propKeys: string | IPropKeysObject[];
}

type TState = Record<string, TPayload>;

export const Subscriber: FC<ISubscriber> = ({ children, events }) => {
    const [props, setProps] = useState<TState>();

    const handleEvent = useCallback(({ payload, propKeys }: IHandleEvent) => {
        const nextState = Array.isArray(propKeys)
            ? createPropsByPayload(payload, propKeys)
            : { [propKeys]: payload };

        setProps(prevState => ({
            ...prevState,
            ...nextState,
        }));
    }, []);

    useLayoutEffect(() => {
        const callbacks = events.reduce<TCallbacks>(
            (result, { name, propKeys }) => ({
                ...result,
                [name]: (payload: TPayload) => handleEvent({ propKeys, payload }),
            }),
            {}
        );

        events.forEach(({ name }) => subscribe(name, callbacks[name]));

        return () => {
            events.forEach(({ name }) => unsubscribe(name, callbacks[name]));
        };
    }, [events, handleEvent]);

    const child = Children.only(children);

    return cloneElement(child, props || child.props);
};
